"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { LocationSearch, type LocationValue } from "../fortune/LocationSearch";
import { TermsAgreement, type TermsState } from "../_common/TermsAgreement";
import { formatPhoneInput, phoneDigits, phoneError, isValidPhone } from "../lib/phone";
import { loadGuestBirth, clearGuestBirth } from "../lib/guestBirth";
import { BrandMark } from "../_common/BrandMark";

type View = "landing" | "phone-login" | "signup-terms" | "signup-form";

const GOOGLE_ERRORS: Record<string, string> = {
  google_unconfigured: "구글 로그인이 아직 설정되지 않았습니다.",
  google_denied: "구글 로그인이 취소되었습니다.",
  google_state_mismatch: "보안 검증에 실패했습니다. 다시 시도해 주세요.",
  google_token_failed: "구글 인증 토큰 교환에 실패했습니다.",
  google_userinfo_failed: "구글 사용자 정보를 가져오지 못했습니다.",
  google_missing_code: "구글 인증 응답이 올바르지 않습니다.",
  google_exception: "구글 로그인 중 오류가 발생했습니다.",
};

const GoogleButton = ({ label = "Google로 계속하기" }: { label?: string }) => (
  <a href="/auth/google"
    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "14px 0", borderRadius: 12,
      background: "#fff", color: "#1f1f1f", border: "1px solid var(--line)", fontSize: 15, fontWeight: 600, textDecoration: "none" }}>
    <svg width={19} height={19} viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /></svg>
    {label}
  </a>
);

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "13px 14px", borderRadius: 12, background: "var(--card-2)",
  border: "1px solid var(--line)", color: "var(--ink)", fontSize: 15, outline: "none", boxSizing: "border-box",
};

export default function LoginPage() {
  return (
    <Suspense fallback={<div style={{ minHeight: "100dvh", background: "var(--bg)" }} />}>
      <LoginInner />
    </Suspense>
  );
}

function LoginInner() {
  const router = useRouter();
  const params = useSearchParams();
  const [view, setView] = useState<View>("landing");

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [unknownTime, setUnknownTime] = useState(false);
  const [location, setLocation] = useState<LocationValue | null>(null);
  const [terms, setTerms] = useState<TermsState>({ terms: false, privacy: false, marketing: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const googleError = params.get("error");
  const googleErrorMsg = googleError ? GOOGLE_ERRORS[googleError] || "로그인 중 오류가 발생했습니다." : null;
  const needMember = params.get("need") === "member";

  // 게스트로 이미 리딩에서 출생정보를 입력해봤다면, 회원가입 폼에 그대로 이어서 채워준다.
  useEffect(() => {
    const draft = loadGuestBirth();
    if (!draft) return;
    setName((v) => v || draft.name || "");
    setGender((v) => v || draft.gender);
    setBirthDate((v) => v || `${draft.year}-${String(draft.month).padStart(2, "0")}-${String(draft.day).padStart(2, "0")}`);
    setUnknownTime((v) => v || !!draft.unknownTime);
    if (!draft.unknownTime) setBirthTime((v) => v || `${String(draft.hour).padStart(2, "0")}:${String(draft.minute).padStart(2, "0")}`);
    setLocation((v) => v || (draft.lat && draft.lon ? { lat: draft.lat, lon: draft.lon, label: draft.cityLabel || "" } : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const digits = phoneDigits(phone);
    if (!isValidPhone(digits)) return setError("전화번호를 정확히 입력하세요.");
    if (password.length < 4) return setError("비밀번호는 4자 이상이어야 합니다.");
    setBusy(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: digits, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { setError(data.message || "로그인에 실패했습니다."); return; }
      clearGuestBirth();
      router.push("/");
      router.refresh();
    } catch {
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function submitSignup(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("이름을 입력하세요.");
    if (!gender) return setError("성별을 선택하세요.");
    if (!birthDate) return setError("생년월일을 입력하세요.");
    if (!unknownTime && !birthTime) return setError("태어난 시간을 입력하거나 '모름'을 선택하세요.");
    if (!location) return setError("태어난 곳을 검색해 선택하세요.");
    const digits = phoneDigits(phone);
    if (!isValidPhone(digits)) return setError("전화번호를 정확히 입력하세요.");
    if (password.length < 4) return setError("비밀번호는 4자 이상이어야 합니다.");

    const [y, mo, d] = birthDate.split("-").map((n) => parseInt(n, 10));
    const [hh, mm] = (unknownTime ? "12:00" : birthTime).split(":").map((n) => parseInt(n, 10));
    const birth = { gender, year: y, month: mo, day: d, hour: hh, minute: mm, unknownTime, lat: location.lat, lon: location.lon, cityLabel: location.label };

    setBusy(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: digits, password, name, birth, agreeTerms: terms.terms, agreePrivacy: terms.privacy, agreeMarketing: terms.marketing }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { setError(data.message || "가입에 실패했습니다."); return; }
      clearGuestBirth();
      router.push("/");
      router.refresh();
    } catch {
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  }

  const shell = (children: React.ReactNode) => (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 18px", background: "var(--bg)" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <Link href="/" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginBottom: 28, textDecoration: "none" }}>
          <BrandMark size={48} />
          <span style={{ fontWeight: 300, fontSize: 20, letterSpacing: 3, color: "var(--ink)" }}>ALIGN</span>
        </Link>
        {needMember && view === "landing" && (
          <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 13, background: "var(--accent-soft)", border: "1px solid var(--line-strong)", fontSize: 13, color: "var(--gold-bright)", lineHeight: 1.6 }}>
            🔒 라이프로그·저장된 인물은 <b>로그인 회원 전용</b> 기능이에요.
          </div>
        )}
        {googleErrorMsg && (
          <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 13, background: "var(--accent-soft)", border: "1px solid rgba(58,74,90,0.4)", fontSize: 13, color: "var(--gold-bright)", lineHeight: 1.6 }}>
            {googleErrorMsg}
          </div>
        )}
        {children}
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <Link href="/" style={{ fontSize: 13, color: "var(--ink-dim)", textDecoration: "none" }}>← 둘러보기로 돌아가기</Link>
        </div>
      </div>
    </div>
  );

  // ── 1) 랜딩: Google(주력) / 전화번호(보조) 두 가지만 ──
  if (view === "landing") {
    return shell(
      <div style={{ borderRadius: 18, background: "var(--card)", border: "1px solid var(--line)", padding: "26px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
        <GoogleButton />
        <button type="button" onClick={() => { setError(null); setView("phone-login"); }}
          style={{ padding: "13px 0", borderRadius: 12, background: "transparent", color: "var(--ink-dim)", border: "1px solid var(--line-strong)", fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
          전화번호로 계속하기
        </button>
        <p style={{ fontSize: 11, color: "var(--ink-faint)", textAlign: "center", marginTop: 6, lineHeight: 1.6 }}>
          비밀번호는 잊어버리면 되찾기 어려우니, 구글 계정으로 시작하는 걸 권장해요.<br />
          가입 시 이용약관과 개인정보 처리방침에 동의하게 됩니다.
        </p>
      </div>
    );
  }

  // ── 2) 전화번호 로그인 ──
  if (view === "phone-login") {
    return shell(
      <form onSubmit={submitLogin} style={{ borderRadius: 18, background: "var(--card)", border: "1px solid var(--line)", padding: "26px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>전화번호로 로그인</div>
        <input value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} placeholder="전화번호" inputMode="numeric" autoComplete="username" style={inputStyle} />
        <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="비밀번호" type="password" autoComplete="current-password" style={inputStyle} />
        {error && <div style={{ fontSize: 13, color: "var(--rose)", lineHeight: 1.5 }}>{error}</div>}
        <button type="submit" disabled={busy}
          style={{ marginTop: 4, padding: "13px 0", borderRadius: 12, border: "1px solid rgba(58,74,90,0.4)", cursor: busy ? "default" : "pointer",
            background: "var(--accent-soft)", color: "var(--gold-bright)", fontSize: 15, fontWeight: 500, opacity: busy ? 0.6 : 1 }}>
          {busy ? "처리 중…" : "로그인"}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <button type="button" onClick={() => { setError(null); setView("landing"); }} style={{ background: "none", border: "none", color: "var(--ink-faint)", fontSize: 12.5, cursor: "pointer", padding: 0 }}>← 뒤로</button>
          <button type="button" onClick={() => { setError(null); setView("signup-terms"); }} style={{ background: "none", border: "none", color: "var(--ink-dim)", fontSize: 12.5, cursor: "pointer", padding: 0 }}>처음이신가요? 회원가입</button>
        </div>
      </form>
    );
  }

  // ── 3) 회원가입 — 약관 동의 먼저 ──
  if (view === "signup-terms") {
    const canProceed = terms.terms && terms.privacy;
    return shell(
      <div style={{ borderRadius: 18, background: "var(--card)", border: "1px solid var(--line)", padding: "26px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>약관 동의</div>
          <p style={{ fontSize: 12.5, color: "var(--ink-dim)", marginTop: 4 }}>계속하려면 필수 항목에 동의해 주세요.</p>
        </div>
        <TermsAgreement value={terms} onChange={setTerms} />
        <button type="button" disabled={!canProceed} onClick={() => setView("signup-form")}
          style={{ padding: "13px 0", borderRadius: 12, border: "1px solid rgba(58,74,90,0.4)", cursor: canProceed ? "pointer" : "default",
            background: "var(--accent-soft)", color: "var(--gold-bright)", fontSize: 15, fontWeight: 500, opacity: canProceed ? 1 : 0.4 }}>
          동의하고 계속하기
        </button>
        <button type="button" onClick={() => setView("phone-login")} style={{ background: "none", border: "none", color: "var(--ink-faint)", fontSize: 12.5, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>← 뒤로</button>
      </div>
    );
  }

  // ── 4) 회원가입 — 실제 정보 입력 ──
  return shell(
    <form onSubmit={submitSignup} style={{ borderRadius: 18, background: "var(--card)", border: "1px solid var(--line)", padding: "26px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)", marginBottom: 2 }}>회원가입</div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="이름" maxLength={24} style={inputStyle} />
      <div style={{ display: "flex", gap: 8 }}>
        {([["male", "남성"], ["female", "여성"]] as const).map(([g, label]) => (
          <button key={g} type="button" onClick={() => setGender(g)}
            style={{ flex: 1, padding: "12px 0", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600,
              background: gender === g ? "var(--accent-soft)" : "var(--card-2)",
              border: gender === g ? "1px solid var(--line-strong)" : "1px solid var(--line)",
              color: gender === g ? "var(--gold-bright)" : "var(--ink-dim)" }}>
            {label}
          </button>
        ))}
      </div>
      <div>
        <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>생년월일</label>
        <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} min="1900-01-01" max="2025-12-31" style={inputStyle} />
      </div>
      <div>
        <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>태어난 시간</label>
        <input type="time" value={birthTime} onChange={(e) => setBirthTime(e.target.value)} disabled={unknownTime}
          style={{ ...inputStyle, opacity: unknownTime ? 0.5 : 1 }} />
        <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, fontSize: 13, color: "var(--ink-dim)", cursor: "pointer" }}>
          <input type="checkbox" checked={unknownTime} onChange={(e) => setUnknownTime(e.target.checked)} style={{ accentColor: "var(--gold)" }} />
          태어난 시간을 몰라요
        </label>
      </div>
      <div>
        <label style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", margin: "0 0 5px 2px" }}>태어난 곳</label>
        <LocationSearch value={location ?? { lat: NaN, lon: NaN, label: "" }} onChange={setLocation} />
      </div>
      <div>
        <input value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} placeholder="전화번호" inputMode="numeric" autoComplete="username" style={inputStyle} />
        {phoneError(phoneDigits(phone)) && <div style={{ fontSize: 11.5, color: "var(--rose)", marginTop: 5 }}>{phoneError(phoneDigits(phone))}</div>}
      </div>
      <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="비밀번호" type="password" autoComplete="new-password" style={inputStyle} />

      {error && <div style={{ fontSize: 13, color: "var(--rose)", lineHeight: 1.5 }}>{error}</div>}

      <button type="submit" disabled={busy}
        style={{ marginTop: 4, padding: "13px 0", borderRadius: 12, border: "1px solid rgba(58,74,90,0.4)", cursor: busy ? "default" : "pointer",
          background: "var(--accent-soft)", color: "var(--gold-bright)", fontSize: 15, fontWeight: 500, opacity: busy ? 0.6 : 1 }}>
        {busy ? "처리 중…" : "가입하고 시작하기"}
      </button>
      <button type="button" onClick={() => setView("signup-terms")} style={{ background: "none", border: "none", color: "var(--ink-faint)", fontSize: 12.5, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>← 뒤로</button>
    </form>
  );
}
